import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  Globe,
  FileCode,
  Users,
  TrendingUp,
  ArrowUpRight,
  Plus,
  Zap,
  BarChart3,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { formatNumber, formatDate, getStatusColor, cn } from '@/lib/utils'

export function DashboardPage() {
  const { user } = useAuthStore()

  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => api.getDomains(),
  })

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: () => api.getAnalyticsOverview(),
  })

  const { data: leadStats, isLoading: leadsLoading } = useQuery({
    queryKey: ['lead-stats'],
    queryFn: () => api.getLeadStats(),
  })

  const stats = [
    {
      name: 'Total Domains',
      value: domains?.domains?.length || 0,
      limit: user?.domainLimit || 2,
      icon: Globe,
      href: '/dashboard/domains',
      color: 'primary',
    },
    {
      name: 'Live Sites',
      value: analytics?.topSites?.filter((s: any) => s.pageviews > 0).length || 0,
      icon: FileCode,
      href: '/dashboard/sites',
      color: 'secondary',
    },
    {
      name: 'Total Visits',
      value: analytics?.overview?.totalPageviews || 0,
      icon: BarChart3,
      href: '/dashboard/analytics',
      color: 'accent',
    },
    {
      name: 'Total Leads',
      value: leadStats?.total || 0,
      icon: Users,
      href: '/dashboard/leads',
      color: 'success',
    },
  ]

  const isLoading = domainsLoading || analyticsLoading || leadsLoading

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.name || 'there'}
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/dashboard/domains" className="btn-outline btn-sm">
            <Plus className="h-4 w-4" />
            Add Domain
          </Link>
          <Link to="/dashboard/generate" className="btn-primary btn-sm">
            <Zap className="h-4 w-4" />
            Generate Site
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.1 }}
          >
            <Link to={stat.href} className="card-hover group block">
              <div className="flex items-start justify-between">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-lg',
                    stat.color === 'primary' && 'bg-primary/10 text-primary',
                    stat.color === 'secondary' && 'bg-secondary/10 text-secondary',
                    stat.color === 'accent' && 'bg-accent/10 text-accent',
                    stat.color === 'success' && 'bg-success/10 text-success'
                  )}
                >
                  <stat.icon className="h-5 w-5" />
                </div>
                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
              <div className="mt-4">
                <p className="text-sm text-muted-foreground">{stat.name}</p>
                <p className="text-2xl font-display font-bold">
                  {isLoading ? '-' : formatNumber(stat.value)}
                  {stat.limit && (
                    <span className="text-sm font-normal text-muted-foreground">
                      /{stat.limit}
                    </span>
                  )}
                </p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Domains */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Recent Domains</h2>
            <Link
              to="/dashboard/domains"
              className="text-sm text-primary hover:underline"
            >
              View all
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-16 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          ) : domains?.domains?.length === 0 ? (
            <div className="py-8 text-center">
              <Globe className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-3">No domains yet</p>
              <Link to="/dashboard/domains" className="btn-primary btn-sm">
                Add Your First Domain
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {domains?.domains?.slice(0, 5).map((domain) => (
                <Link
                  key={domain.id}
                  to={`/dashboard/domains/${domain.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Globe className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium">{domain.domain_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {domain.monetization_strategy || 'Not configured'}
                      </p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      'badge',
                      `badge-${getStatusColor(domain.verification_status)}`
                    )}
                  >
                    {domain.verification_status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Recent Activity</h2>
            <Link
              to="/dashboard/analytics"
              className="text-sm text-primary hover:underline"
            >
              View all
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-12 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          ) : analytics?.recentEvents?.length === 0 ? (
            <div className="py-8 text-center">
              <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No activity yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {analytics?.recentEvents?.slice(0, 8).map((activity: any, i: number) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full',
                      activity.type === 'pageview'
                        ? 'bg-primary/10 text-primary'
                        : activity.type === 'lead'
                          ? 'bg-success/10 text-success'
                          : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {activity.type === 'pageview' ? (
                      <BarChart3 className="h-4 w-4" />
                    ) : activity.type === 'lead' ? (
                      <Users className="h-4 w-4" />
                    ) : (
                      <TrendingUp className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {activity.type === 'pageview'
                        ? 'Page view'
                        : activity.type === 'lead'
                          ? 'New lead captured'
                          : activity.type}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {activity.domainName || activity.siteId}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(Math.floor((activity.timestamp || Date.now()) / 1000))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      {user?.subscriptionTier === 'free' && (
        <div className="card bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold mb-1">Upgrade to Pro</h3>
              <p className="text-sm text-muted-foreground">
                Get 10 domains, YOLO batch mode, and advanced analytics
              </p>
            </div>
            <Link to="/dashboard/settings/billing" className="btn-primary btn-sm">
              Upgrade Now
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
